/**
 * teamNameParser
 *
 * Parses a coach's free-text team name into structured fields.
 * Pure, dependency-free, and intentionally not wired into any UI.
 */

export type ParseConfidence = "high" | "medium" | "low";

export interface ParseTeamNameOptions {
  eventYear: number;
  existingClubs?: string[];
}

export type ParsedGender = "boys" | "girls" | null;

export interface ParsedTeamName {
  club: string;
  variation: string | null;
  ageGroup: string | null;
  /** Gender read out of the typed text — never left in the name. */
  gender: ParsedGender;
  confidence: ParseConfidence;
  assumption: string | null;
}

/**
 * Lowercase, strip . ' - | ( ), collapse whitespace, drop a leading "the".
 */
export function normalizeForMatch(s: string): string {
  if (!s) return "";
  let out = s
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[.\-|()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  out = out.replace(/^the\s+/, "");
  return out.trim();
}

const GENDER_TOKENS = new Set([
  "boys",
  "boy",
  "girls",
  "girl",
  "mens",
  "men",
  "womens",
  "women",
  "ladies",
  "bg",
]);

const GIRL_TOKENS = new Set(["girls", "girl", "womens", "women", "ladies"]);
const BOY_TOKENS = new Set(["boys", "boy", "mens", "men"]);

/** True when a token is a gender word (apostrophes and case ignored). */
export function isGenderToken(token: string): boolean {
  return GENDER_TOKENS.has(normalizeForMatch(token));
}

/** Gender word carried by a free-text name, if any. */
export function extractGender(raw: string): "boys" | "girls" | null {
  for (const t of (raw ?? "").split(/[\s\-|,/:()]+/)) {
    const n = normalizeForMatch(t);
    if (GIRL_TOKENS.has(n)) return "girls";
    if (BOY_TOKENS.has(n)) return "boys";
  }
  return null;
}

/** Remove every gender word from a fragment. */
export function stripGenderWords(s: string): string {
  return (s ?? "")
    .split(/\s+/)
    .filter((t) => t && !isGenderToken(t))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}


const AGE_TOKEN_RE = /^(\d{1,2})\s?u\b/i;
const AGE_TOKEN_GREEDY_RE = /^(\d{1,2})u(.*)$/i;
const GRADE_RE = /^(\d{1,2})(st|nd|rd|th)$/i;

function tokenize(raw: string): string[] {
  return raw.trim().split(/\s+/).filter(Boolean);
}

function joinTokens(tokens: string[]): string {
  return tokens.join(" ").replace(/\s+/g, " ").trim();
}

/** Strip separator-ish leftovers at the edges of a fragment. */
function cleanFragment(s: string): string {
  return s
    .replace(/^[\s\-|,/:()]+/, "")
    .replace(/[\s\-|,/:()]+$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function ageGroupLabel(age: number): string {
  return `${age}u`;
}

function ageFromLabel(label: string): number | null {
  const m = /^(\d{1,2})\s?u$/i.exec(label.trim());
  return m ? parseInt(m[1], 10) : null;
}

/** Find a contiguous token range whose normalized join equals `needle`. */
function findTokenRange(
  tokens: string[],
  needle: string
): { start: number; end: number } | null {
  if (!needle) return null;
  for (let start = 0; start < tokens.length; start++) {
    for (let end = start + 1; end <= tokens.length; end++) {
      const candidate = normalizeForMatch(tokens.slice(start, end).join(" "));
      if (candidate === needle) return { start, end };
      if (candidate.length > needle.length) break;
    }
  }
  return null;
}

interface ClubMatch {
  club: string;
  start: number;
  end: number;
}

function matchExistingClub(
  tokens: string[],
  existingClubs: string[]
): ClubMatch | null {
  const candidates = existingClubs
    .map((c) => ({ original: c.trim(), norm: normalizeForMatch(c) }))
    .filter((c) => c.norm.length > 0)
    // longest normalized club wins; prefer earlier position afterwards
    .sort((a, b) => b.norm.length - a.norm.length);

  for (const cand of candidates) {
    const range = findTokenRange(tokens, cand.norm);
    if (range) {
      return { club: cand.original, start: range.start, end: range.end };
    }
  }
  return null;
}

interface AgeResult {
  age: number;
  /** index of the token that carried the age, in the token array searched */
  index: number;
  /** leftover text carved off the age token, e.g. "17URL" -> "RL" */
  remainder: string | null;
  /** number of tokens consumed starting at index (handles "16 U") */
  span: number;
  confidence: ParseConfidence;
  assumption: string | null;
}

function extractAge(tokens: string[], eventYear: number): AgeResult | null {
  // 1. Age token: 14U, 14 U, 17URL
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const greedy = AGE_TOKEN_GREEDY_RE.exec(t);
    if (greedy) {
      const remainder = cleanFragment(greedy[2] ?? "");
      return {
        age: parseInt(greedy[1], 10),
        index: i,
        remainder: remainder || null,
        span: 1,
        confidence: "high",
        assumption: null,
      };
    }
    if (/^\d{1,2}$/.test(t) && i + 1 < tokens.length && /^u$/i.test(tokens[i + 1])) {
      return {
        age: parseInt(t, 10),
        index: i,
        remainder: null,
        span: 2,
        confidence: "high",
        assumption: null,
      };
    }
    if (AGE_TOKEN_RE.test(t)) {
      const m = AGE_TOKEN_RE.exec(t)!;
      return {
        age: parseInt(m[1], 10),
        index: i,
        remainder: cleanFragment(t.slice(m[0].length)) || null,
        span: 1,
        confidence: "high",
        assumption: null,
      };
    }
  }

  // 2. Graduation year
  for (let i = 0; i < tokens.length; i++) {
    const m = /^(\d{4})$/.exec(tokens[i]);
    if (!m) continue;
    const year = parseInt(m[1], 10);
    if (year >= eventYear && year <= eventYear + 12) {
      const age = 17 - (year - (eventYear + 1));
      return {
        age,
        index: i,
        remainder: null,
        span: 1,
        confidence: "medium",
        assumption: `We read ${year} as a graduation year.`,
      };
    }
  }

  // 3. Grade
  for (let i = 0; i < tokens.length; i++) {
    const m = GRADE_RE.exec(tokens[i]);
    if (!m) continue;
    const grade = parseInt(m[1], 10);
    return {
      age: grade + 6,
      index: i,
      remainder: null,
      span: 1,
      confidence: "medium",
      assumption: `We read ${grade} as ${m[1]}${m[2].toLowerCase()} grade.`,
    };
  }

  return null;
}

function isFillerOnly(tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  return tokens.every((t) => {
    const n = normalizeForMatch(t);
    return n === "" || GENDER_TOKENS.has(n);
  });
}

export function parseTeamName(
  input: string,
  opts: ParseTeamNameOptions
): ParsedTeamName {
  const empty: ParsedTeamName = {
    club: "",
    variation: null,
    ageGroup: null,
    gender: null,
    confidence: "low",
    assumption: null,
  };
  if (!input || !input.trim()) return empty;

  const raw = input.trim().replace(/\s+/g, " ");
  const existingClubs = opts.existingClubs ?? [];

  // --- Parenthetical is hard evidence for a variation; pull it out early.
  // If the bracketed group carries the age, that age wins and the rest of the
  // group becomes the variation — the parentheses rule beats everything else.
  let parenVariation: string | null = null;
  let parenAge: AgeResult | null = null;
  let working = raw;
  const parenMatch = /\(([^)]*)\)/.exec(raw);
  if (parenMatch) {
    const parenTokens = tokenize(parenMatch[1]);
    parenAge = extractAge(parenTokens, opts.eventYear);
    let leftover = parenTokens;
    if (parenAge) {
      leftover = [];
      for (let i = 0; i < parenTokens.length; i++) {
        if (i >= parenAge.index && i < parenAge.index + parenAge.span) {
          if (i === parenAge.index && parenAge.remainder) leftover.push(parenAge.remainder);
          continue;
        }
        leftover.push(parenTokens[i]);
      }
    }
    parenVariation = cleanFragment(joinTokens(leftover)) || null;
    working = cleanFragment(raw.replace(parenMatch[0], " "));
  }

  const tokens = tokenize(working);

  // --- 1. Match against existing clubs FIRST.
  const clubMatch = existingClubs.length
    ? matchExistingClub(tokens, existingClubs)
    : null;

  let club: string | null = null;
  let variationTokens: string[] = [];
  let searchTokens = tokens;
  let clubStartsInput = false;

  if (clubMatch) {
    club = clubMatch.club;
    clubStartsInput = clubMatch.start === 0;
    const before = tokens.slice(0, clubMatch.start);
    const after = tokens.slice(clubMatch.end);
    searchTokens = [...before, ...after];
  }

  // --- 2. Extract the age from whatever is left (unless the parens supplied it).
  const ageResult = parenAge ? null : extractAge(searchTokens, opts.eventYear);


  let ageGroup: string | null = null;
  let confidence: ParseConfidence = "high";
  let assumption: string | null = null;

  if (ageResult) {
    ageGroup = ageGroupLabel(ageResult.age);
    confidence = ageResult.confidence;
    assumption = ageResult.assumption;
  } else if (parenAge) {
    ageGroup = ageGroupLabel(parenAge.age);
    confidence = parenAge.confidence;
    assumption = parenAge.assumption;
  } else {
    ageGroup = null;
    confidence = "low";
  }


  // Tokens with the age removed, keeping any remainder carved off the age token.
  const withoutAge: string[] = [];
  const preAge: string[] = [];
  const postAge: string[] = [];
  if (ageResult) {
    for (let i = 0; i < searchTokens.length; i++) {
      if (i >= ageResult.index && i < ageResult.index + ageResult.span) {
        if (i === ageResult.index && ageResult.remainder) {
          postAge.push(ageResult.remainder);
          withoutAge.push(ageResult.remainder);
        }
        continue;
      }
      if (i < ageResult.index) preAge.push(searchTokens[i]);
      else postAge.push(searchTokens[i]);
      withoutAge.push(searchTokens[i]);
    }
  } else {
    withoutAge.push(...searchTokens);
  }

  if (club) {
    // Club already resolved — everything else that isn't age or filler is variation.
    variationTokens = withoutAge.filter((t) => !GENDER_TOKENS.has(normalizeForMatch(t)));
    // A known club at the front of the input is itself hard evidence for a split.
    void clubStartsInput;
  } else {
    const rest = joinTokens(withoutAge);
    // Hard evidence: an explicit " - " style separator.
    const dashSplit = splitOnDash(rest);
    if (dashSplit) {
      club = dashSplit[0];
      variationTokens = tokenize(dashSplit[1]);
    } else if (ageResult) {
      const preClean = preAge.filter((t) => !GENDER_TOKENS.has(normalizeForMatch(t)));
      if (isFillerOnly(preAge) || preClean.length === 0) {
        // Age led the string: the remainder is the club, no hard split evidence.
        club = joinTokens(postAge);
        variationTokens = [];
      } else {
        club = joinTokens(preClean);
        variationTokens = postAge;
      }
    } else {
      // No hard evidence at all — the whole string is the club.
      club = joinTokens(
        withoutAge.filter((t) => !GENDER_TOKENS.has(normalizeForMatch(t)))
      );
      variationTokens = [];
    }
  }

  let variation = cleanFragment(joinTokens(variationTokens)) || null;
  if (parenVariation) {
    variation = variation ? `${parenVariation} ${variation}` : parenVariation;
  }

  club = cleanFragment(club ?? "");

  // Standing rule: a stored name never carries a gender word, wherever it sat
  // in the typed text. The gender it implied becomes a field instead.
  const gender = extractGender(raw);
  club = cleanFragment(stripGenderWords(club));
  variation = variation ? cleanFragment(stripGenderWords(variation)) || null : null;

  return {
    club,
    variation,
    ageGroup,
    gender,
    confidence,
    assumption,
  };
}

/** Split on a standalone dash separator, e.g. "Club - Elite". */
function splitOnDash(s: string): [string, string] | null {
  const m = /^(.*?)\s+[-–—|]\s+(.*)$/.exec(s);
  if (!m) return null;
  const left = cleanFragment(m[1]);
  const right = cleanFragment(m[2]);
  if (!left || !right) return null;
  return [left, right];
}
